import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  ChangeDetectionStrategy
} from '@angular/core';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { debounceTime, map, startWith, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';

/**
 * This component allows you to select value from the options
 * or add a new one
 */

@Component({
  selector: 'aid-autocomplete',
  template: `
    <mat-form-field class="full-width">
      @if (label && applyPipes) {
        <mat-label>{{ label | translate | titlecase }}</mat-label>
      }
      @if (label && !applyPipes) {
        <mat-label>{{ label }}</mat-label>
      }

      <input
        [required]="isRequired"
        matInput
        #input
        [value]="uppercase ? input.value.toUpperCase() : input.value"
        [matAutocomplete]="auto"
        [formControl]="control"
        data-cy="autocomplete-customer-location"
      />
      @if (loading) {
        <mat-spinner class="select-search-spinner" diameter="16"> </mat-spinner>
      }
      <mat-autocomplete
        #auto="matAutocomplete"
        (optionSelected)="onAddOption()"
        [autoActiveFirstOption]="true"
      >
        @if (!options || (options.length === 0 && !control.value)) {
          <mat-option>
            {{ notFound | translate | sentancecase }}
          </mat-option>
        }

        @for (item of filteredItems | async; track item) {
          <mat-option [value]="item">
            @if (translate && item) {
              <span>
                {{ item | translate | sentancecase }}
              </span>
            }
            @if (!translate && item) {
              <span> {{ item }} </span>
            }
          </mat-option>
        }
      </mat-autocomplete>
      @if (control.hasError('required')) {
        <mat-error>
          {{ label || 'this-field' | translate | titlecase }}
          {{ 'is-required' | translate }}
        </mat-error>
      }
      @for (error of control.errors | keyvalue; track error) {
        <mat-error>
          @if (displayError(error.key)) {
            {{ error.value | translate }}
          }
        </mat-error>
      }
    </mat-form-field>
  `,
  styleUrls: ['./autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AutocompleteComponent implements OnInit, OnDestroy {
  @Input('options') set items(options: string[] | null | undefined) {
    if (options) {
      this.options = options;
      this.initFilter(options);
    }
  }

  constructor() {}
  // Placeholder until the required control is bound; options set before that
  // are not filtered.
  private readonly unboundControl = new UntypedFormControl();
  @Input({ required: true }) control: UntypedFormControl = this.unboundControl;
  @Input() label?: string;
  @Input() applyPipes = true;
  @Input() translate = false;
  @Input() loading?: boolean | null;
  @Input() notFound = 'no-items-found';
  @Input() uppercase = false;

  filteredItems: Observable<string[]> | null = null;
  options?: string[];

  private ngUnsubscribe = new Subject<void>();
  @Input() displayFn: (item: { name?: string }) => string | undefined = item =>
    item.name;

  ngOnInit() {}

  get isRequired(): boolean {
    if (this.control.validator) {
      const validator = this.control.validator({} as AbstractControl);
      if (validator && validator.required) {
        return true;
      }
    }

    return false;
  }

  initFilter(options: string[]) {
    this.filteredItems =
      this.control !== this.unboundControl
        ? this.control.valueChanges.pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(1000),
            startWith(''),
            map(item =>
              item ? this.filterItems(item, options) : options.slice()
            )
          )
        : null;
  }

  filterItems(name: string, options: string[]) {
    return options.filter(item =>
      item ? item.toLowerCase().indexOf(name.toLowerCase()) === 0 : null
    );
  }

  onAddOption() {
    const options = this.options;
    if (!this.control.value || !options) {
      return;
    }
    if (!options.some(entry => entry === this.control.value)) {
      const index = options.push(this.control.value) - 1;
      this.control.setValue(options[index]);
    }
  }

  displayError(error: string): boolean {
    return !(error === 'required');
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
